import { ChevronDown, ChevronUp, Copy, GripVertical, Info, SquarePen, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Task } from '@/types/experience';

interface TaskCardProps {
  task: Task;
  isReorderMode?: boolean;
  isSelected?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onDuplicate: (taskId: string) => void;
}

export default function TaskCard({
  task,
  isReorderMode = false,
  isSelected = false,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  onDuplicate,
}: TaskCardProps) {
  const { t } = useTranslation();

  if (isReorderMode) {
    return (
      <div className='flex items-center gap-2'>
        <GripVertical className='hidden h-5 w-5 shrink-0 cursor-grab text-ws-muted md:block' />
        <div className={`flex w-full items-center gap-2 rounded-lg border border-ws-border px-3 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${isSelected ? 'bg-ws-hover' : 'bg-ws-surface'}`}>
          <p className='flex-1 overflow-hidden text-ellipsis text-base font-normal leading-5 text-ws-text'>
            {task.title}
          </p>
          <div className='hidden max-md:flex flex-col'>
            <button
              onClick={onMoveUp}
              disabled={!onMoveUp}
              className='flex h-5 w-5 items-center justify-center rounded hover:bg-ws-hover disabled:opacity-20'
              aria-label={t('task.moveUp')}
            >
              <ChevronUp className='h-4 w-4 text-ws-muted' />
            </button>
            <button
              onClick={onMoveDown}
              disabled={!onMoveDown}
              className='flex h-5 w-5 items-center justify-center rounded hover:bg-ws-hover disabled:opacity-20'
              aria-label={t('task.moveDown')}
            >
              <ChevronDown className='h-4 w-4 text-ws-muted' />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-lg rounded-lg border border-ws-border shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${isSelected ? 'bg-ws-hover' : 'bg-ws-surface'}`}>
      <div className='flex flex-col gap-4 px-4 py-3'>
        <div className='flex items-start justify-between gap-2'>
          <p className='overflow-hidden text-ellipsis text-base font-normal leading-5 text-ws-text'>
            {task.title}
          </p>
          <div className='group relative shrink-0'>
            <Info className='h-3.5 w-3.5 text-ws-muted opacity-40 hover:opacity-100 cursor-default' />
            <div className='pointer-events-none absolute right-0 top-5 z-50 hidden w-56 rounded-md border border-ws-border bg-ws-surface p-2.5 shadow-md group-hover:block'>
              <p className='text-[10px] font-medium uppercase tracking-wide text-ws-muted mb-1.5'>{t('experience.debug.title')}</p>
              <div className='space-y-1'>
                <div>
                  <span className='text-[10px] text-ws-muted'>{t('experience.debug.id')}</span>
                  <p className='font-mono text-[11px] text-ws-text break-all'>{task.id}</p>
                </div>
                <div>
                  <span className='text-[10px] text-ws-muted'>{t('experience.debug.heritage')}</span>
                  <p className='font-mono text-[11px] text-ws-text break-all'>{task.heritageId ?? t('experience.debug.original')}</p>
                </div>
                <div>
                  <span className='text-[10px] text-ws-muted'>{t('experience.debug.author')}</span>
                  <p className='font-mono text-[11px] text-ws-text break-all'>{task.authorId ?? '—'}</p>
                </div>
                <div>
                  <span className='text-[10px] text-ws-muted'>{t('experience.card.version')}</span>
                  <p className='font-mono text-[11px] text-ws-text'>v{task.version ?? 1}</p>
                </div>
                <div>
                  <span className='text-[10px] text-ws-muted'>{t('experience.debug.created')}</span>
                  <p className='font-mono text-[11px] text-ws-text'>{task.createdAt ? new Date(task.createdAt).toLocaleString() : '—'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex items-center justify-between'>
          <button
            onClick={() => onEdit(task)}
            className='flex h-9 items-center gap-2 rounded-md bg-ws-primary px-4 text-base font-medium leading-5 text-white hover:opacity-90'
          >
            <SquarePen className='h-4 w-4' />
            {t('common.edit')}
          </button>

          <div className='flex items-center gap-[6px] pl-[6px]'>
            <button
              onClick={() => onDuplicate(task.id)}
              className='flex h-9 w-9 items-center justify-center rounded-md border border-ws-border bg-ws-control-contained hover:bg-ws-hover'
              aria-label={t('experience.card.duplicate')}
            >
              <Copy strokeWidth={1.25} className='h-4 w-4 text-ws-text' />
            </button>
            <button
              onClick={() => onDelete(task.id)}
              className='flex h-9 w-9 items-center justify-center rounded-md border border-ws-border bg-ws-control-contained hover:bg-red-50 dark:hover:bg-red-900/30'
              aria-label={t('experience.card.delete')}
            >
              <Trash2 strokeWidth={1.25} className='h-4 w-4 text-ws-text' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
